import DateTimePicker from '@react-native-community/datetimepicker';
import moment from 'moment';
import React, {useEffect, useState} from 'react';
import {
  ActivityIndicator,
  FlatList,
  Text,
  TouchableOpacity,
  View,
  StyleSheet,
  ScrollView,
  Dimensions,
} from 'react-native';
import {LineChart} from 'react-native-chart-kit';
import {Card} from 'react-native-paper';
import FontAwesome from '@react-native-vector-icons/fontawesome';
import {useSelector} from 'react-redux';
import {useGetFORevenueQuery} from '../redux/services/foreportsApi';
import {RootState} from '../redux/store';
import {theme} from '../theme/theme';

import T from '../Components/T';
const screenWidth = Dimensions.get('window').width;

const RevenueSummary = () => {
  const gdata = useSelector((state: RootState) => state.gdata);
  const cid = gdata.company;

  const [sdate, setSdate] = useState<Date>(new Date());
  const [edate, setEdate] = useState<Date>(new Date());
  const [showStartPicker, setShowStartPicker] = useState(false);
  const [showEndPicker, setShowEndPicker] = useState(false);

  const {data, isLoading} = useGetFORevenueQuery({
    cid: cid,
    fdt: moment(sdate).format('YYYY-MM-DD'),
    tdt: moment(edate).format('YYYY-MM-DD'),
  });

  const [chartData, setChartData] = useState([]);
  // Data loaded for revenue summary
  useEffect(() => {
    if (data) {
      const formattedData = data
        ?.filter((v: any) => !isNaN(parseFloat(v.rv_total)))
        ?.map((entry: any) => ({
          label: entry.rv_head?.substring(0, 5) || '',
          value: parseFloat(entry.rv_total) || 0,
        }));
      setChartData(formattedData ?? []);
    }
  }, [data]);

  const rows = data
    ?.filter((v: any) => v.rv_total > 0 || !isNaN(parseFloat(v.rv_total)))
    ?.map((value: any, key: any) => ({
      id: key.toString(),
      rv_head: value.rv_head,
      rv_rate: Number(value.rv_rate).toFixed(2),
      rv_discamt: Number(value.rv_discamt).toFixed(2),
      rv_taxable: Number(value.rv_taxable).toFixed(2),
      rv_taxper: Number(value.rv_taxper || 0).toFixed(2),
      rv_tax1amt: Number(value.rv_tax1amt).toFixed(2),
      rv_tax2amt: Number(value.rv_tax2amt).toFixed(2),
      rv_tax3amt: Number(value.rv_tax3amt).toFixed(2),
      rv_roff: Number(value.rv_roff).toFixed(2),
      rv_total: Number(value.rv_total).toFixed(2),
    }));

  // Calculate totals
  const totalRevenue =
    rows?.reduce((sum, item) => sum + parseFloat(item.rv_total), 0) || 0;
  const totalAmount =
    rows?.reduce((sum, item) => sum + parseFloat(item.rv_rate), 0) || 0;
  const totalDiscount =
    rows?.reduce((sum, item) => sum + parseFloat(item.rv_discamt), 0) || 0;
  const totalTaxable =
    rows?.reduce((sum, item) => sum + parseFloat(item.rv_taxable), 0) || 0;
  const totalTax =
    rows?.reduce(
      (sum, item) =>
        sum +
        parseFloat(item.rv_tax1amt) +
        parseFloat(item.rv_tax2amt) +
        parseFloat(item.rv_tax3amt),
      0,
    ) || 0;

  if (isLoading) {
    return (
      <View style={styles.loaderContainer}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
        <Text style={styles.loadingText}><T>Loading revenue data...</T></Text>
      </View>
    );
  }

  const renderRevenueCard = ({item}: {item: any}) => (
    <Card style={styles.revenueCard} elevation={3}>
      <View style={styles.cardHeader}>
        <View style={styles.revenueIconContainer}>
          <FontAwesome
            name="rupee"
            size={20}
            color={theme.colors.primary}
          />
        </View>
        <Text style={styles.revenueTitle}>{item.rv_head}</Text>
        <View style={styles.totalBadge}>
          <Text style={styles.totalBadgeText}>₹{item.rv_total}</Text>
        </View>
      </View>

      <View style={styles.cardBody}>
        <View style={styles.detailRow}>
          <View style={styles.detailItem}>
            <Text style={styles.detailLabel}><T>Amount</T></Text>
            <Text style={styles.detailValue}>₹{item.rv_rate}</Text>
          </View>
          <View style={styles.detailItem}>
            <Text style={styles.detailLabel}><T>Discount</T></Text>
            <Text style={[styles.detailValue, styles.discountValue]}>
              -₹{item.rv_discamt}
            </Text>
          </View>
        </View>

        <View style={styles.detailRow}>
          <View style={styles.detailItem}>
            <Text style={styles.detailLabel}><T>Taxable</T></Text>
            <Text style={[styles.detailValue, {color: theme.colors.info}]}>
              ₹{item.rv_taxable}
            </Text>
          </View>
          <View style={styles.detailItem}>
            <Text style={styles.detailLabel}><T>Tax %</T></Text>
            <Text style={[styles.detailValue, {color: theme.colors.secondary}]}>
              {item.rv_taxper}%
            </Text>
          </View>
        </View>

        <View style={styles.taxContainer}>
          <View style={styles.taxRow}>
            <FontAwesome
              name="file-text-o"
              size={14}
              color={theme.colors.textTertiary}
            />
            <Text style={styles.taxLabel}><T>CGST:</T></Text>
            <Text style={styles.taxValue}>₹{item.rv_tax1amt}</Text>
          </View>
          <View style={styles.taxRow}>
            <FontAwesome
              name="file-text-o"
              size={14}
              color={theme.colors.textTertiary}
            />
            <Text style={styles.taxLabel}><T>SGST:</T></Text>
            <Text style={styles.taxValue}>₹{item.rv_tax2amt}</Text>
          </View>
          {parseFloat(item.rv_tax3amt) > 0 && (
            <View style={styles.taxRow}>
              <FontAwesome
                name="file-text-o"
                size={14}
                color={theme.colors.textTertiary}
              />
              <Text style={styles.taxLabel}><T>Tax 3:</T></Text>
              <Text style={styles.taxValue}>₹{item.rv_tax3amt}</Text>
            </View>
          )}
          {parseFloat(item.rv_roff) > 0 && (
            <View style={styles.taxRow}>
              <FontAwesome
                name="minus-circle"
                size={14}
                color={theme.colors.error}
              />
              <Text style={styles.taxLabel}><T>R.Off:</T></Text>
              <Text style={[styles.taxValue, styles.roundOffValue]}>
                ₹{item.rv_roff}
              </Text>
            </View>
          )}
        </View>
      </View>
    </Card>
  );

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerContent}>
          <View style={styles.headerIconContainer}>
            <FontAwesome
              name="bar-chart"
              size={28}
              color={theme.colors.white}
            />
          </View>
          <View style={styles.headerTextContainer}>
            <Text style={styles.headerTitle}><T>Revenue Summary</T></Text>
            <Text style={styles.headerSubtitle}>
              {moment(sdate).format('DD MMM')} -{' '}
              {moment(edate).format('DD MMM YYYY')}
            </Text>
          </View>
        </View>
      </View>

      {/* Date Selection */}
      <View style={styles.dateContainer}>
        <TouchableOpacity
          style={styles.dateButton}
          onPress={() => setShowStartPicker(true)}>
          <FontAwesome
            name="calendar"
            size={16}
            color={theme.colors.primary}
            style={styles.dateIcon}
          />
          <View style={styles.dateButtonContent}>
            <Text style={styles.dateLabel}><T>From Date</T></Text>
            <Text style={styles.dateValue}>
              {moment(sdate).format('DD MMM YYYY')}
            </Text>
          </View>
          <FontAwesome
            name="chevron-down"
            size={14}
            color={theme.colors.textTertiary}
          />
        </TouchableOpacity>

        <View style={styles.dateSeparator} />

        <TouchableOpacity
          style={styles.dateButton}
          onPress={() => setShowEndPicker(true)}>
          <FontAwesome
            name="calendar"
            size={16}
            color={theme.colors.primary}
            style={styles.dateIcon}
          />
          <View style={styles.dateButtonContent}>
            <Text style={styles.dateLabel}><T>To Date</T></Text>
            <Text style={styles.dateValue}>
              {moment(edate).format('DD MMM YYYY')}
            </Text>
          </View>
          <FontAwesome
            name="chevron-down"
            size={14}
            color={theme.colors.textTertiary}
          />
        </TouchableOpacity>
      </View>

      {showStartPicker && (
        <DateTimePicker
          value={sdate}
          mode="date"
          display="default"
          onChange={(event, selectedDate) => {
            setShowStartPicker(false);
            if (selectedDate) setSdate(selectedDate);
          }}
        />
      )}

      {showEndPicker && (
        <DateTimePicker
          value={edate}
          mode="date"
          display="default"
          onChange={(event, selectedDate) => {
            setShowEndPicker(false);
            if (selectedDate) setEdate(selectedDate);
          }}
        />
      )}

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}>
        {/* Summary Cards */}
        <View style={styles.summaryContainer}>
          <Card style={styles.summaryCard} elevation={2}>
            <View style={styles.summaryIconContainer}>
              <FontAwesome
                name="rupee"
                size={24}
                color={theme.colors.success}
              />
            </View>
            <Text style={styles.summaryLabel}><T>Total Revenue</T></Text>
            <Text style={styles.summaryValue}>₹{totalRevenue.toFixed(2)}</Text>
          </Card>

          <Card style={styles.summaryCard} elevation={2}>
            <View
              style={[
                styles.summaryIconContainer,
                {backgroundColor: theme.colors.infoLight},
              ]}>
              <FontAwesome
                name="money"
                size={24}
                color={theme.colors.info}
              />
            </View>
            <Text style={styles.summaryLabel}><T>Total Amount</T></Text>
            <Text style={styles.summaryValue}>₹{totalAmount.toFixed(2)}</Text>
          </Card>
        </View>

        <View style={styles.summaryContainer}>
          <Card style={styles.summaryCard} elevation={2}>
            <View
              style={[
                styles.summaryIconContainer,
                {backgroundColor: theme.colors.warningLight},
              ]}>
              <FontAwesome
                name="tag"
                size={24}
                color={theme.colors.warning}
              />
            </View>
            <Text style={styles.summaryLabel}><T>Total Discount</T></Text>
            <Text style={styles.summaryValue}>₹{totalDiscount.toFixed(2)}</Text>
          </Card>

          <Card style={styles.summaryCard} elevation={2}>
            <View
              style={[
                styles.summaryIconContainer,
                {backgroundColor: theme.colors.infoLight},
              ]}>
              <FontAwesome
                name="calculator"
                size={24}
                color={theme.colors.info}
              />
            </View>
            <Text style={styles.summaryLabel}><T>Total Taxable</T></Text>
            <Text style={styles.summaryValue}>₹{totalTaxable.toFixed(2)}</Text>
          </Card>
        </View>

        <View style={styles.summaryContainer}>
          <Card style={styles.summaryCard} elevation={2}>
            <View
              style={[
                styles.summaryIconContainer,
                {backgroundColor: theme.colors.secondary + '20'},
              ]}>
              <FontAwesome
                name="percent"
                size={24}
                color={theme.colors.secondary}
              />
            </View>
            <Text style={styles.summaryLabel}><T>Total Tax</T></Text>
            <Text style={styles.summaryValue}>₹{totalTax.toFixed(2)}</Text>
          </Card>

          <View style={{flex: 1}} />
        </View>

        {/* Revenue Chart */}
        {chartData.length > 0 && (
          <Card style={styles.chartCard} elevation={2}>
            <View style={styles.chartHeader}>
              <FontAwesome
                name="line-chart"
                size={20}
                color={theme.colors.primary}
              />
              <Text style={styles.chartTitle}><T>Revenue Distribution</T></Text>
            </View>
            <LineChart
              data={{
                labels: chartData.map((entry: any) => entry.label),
                datasets: [
                  {data: chartData.map((entry: any) => entry.value)},
                ],
              }}
              width={screenWidth - 64}
              height={220}
              yAxisLabel="₹"
              yAxisSuffix=""
              chartConfig={{
                backgroundColor: theme.colors.white,
                backgroundGradientFrom: theme.colors.primary,
                backgroundGradientTo: theme.colors.secondary,
                decimalPlaces: 0,
                color: (opacity = 1) => `rgba(255, 255, 255, ${opacity})`,
                labelColor: (opacity = 1) => `rgba(255, 255, 255, ${opacity})`,
                strokeWidth: 2,
                barPercentage: 0.7,
              }}
              bezier
              style={styles.chart}
            />
          </Card>
        )}

        {/* Revenue Details */}
        {rows && rows.length > 0 ? (
          <View style={styles.revenueListContainer}>
            <View style={styles.sectionHeader}>
              <FontAwesome
                name="list-alt"
                size={20}
                color={theme.colors.textPrimary}
              />
              <Text style={styles.sectionTitle}><T>Revenue Details</T></Text>
              <Text style={styles.sectionSubtitle}>
                {rows.length} {rows.length === 1 ? 'item' : 'items'}
              </Text>
            </View>
            <FlatList
              data={rows}
              keyExtractor={item => item.id}
              renderItem={renderRevenueCard}
              scrollEnabled={false}
            />
          </View>
        ) : (
          <View style={styles.emptyContainer}>
            <FontAwesome
              name="bar-chart"
              size={64}
              color={theme.colors.mediumGray}
            />
            <Text style={styles.emptyText}><T>No revenue data available</T></Text>
            <Text style={styles.emptySubtext}>
              <T>Select a date range to view revenue summary</T>
            </Text>
          </View>
        )}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.backgroundSecondary,
  },
  loaderContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.colors.backgroundSecondary,
  },
  loadingText: {
    marginTop: theme.spacing.base,
    fontSize: theme.typography.fontSize.base,
    color: theme.colors.textSecondary,
    fontWeight: theme.typography.fontWeight.medium,
  },
  header: {
    backgroundColor: theme.colors.primary,
    paddingTop: theme.spacing['2xl'],
    paddingBottom: theme.spacing.xl,
    paddingHorizontal: theme.spacing.xl,
    ...theme.shadows.md,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerIconContainer: {
    width: 56,
    height: 56,
    borderRadius: theme.borderRadius.full,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: theme.spacing.base,
  },
  headerTextContainer: {
    flex: 1,
  },
  headerTitle: {
    fontSize: theme.typography.fontSize['2xl'],
    fontWeight: theme.typography.fontWeight.bold,
    color: theme.colors.white,
    marginBottom: theme.spacing.xs,
  },
  headerSubtitle: {
    fontSize: theme.typography.fontSize.sm,
    color: 'rgba(255, 255, 255, 0.9)',
    fontWeight: theme.typography.fontWeight.regular,
  },
  dateContainer: {
    flexDirection: 'row',
    margin: theme.spacing.base,
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.xl,
    padding: theme.spacing.xs,
    ...theme.shadows.sm,
  },
  dateButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    padding: theme.spacing.base,
  },
  dateIcon: {
    marginRight: theme.spacing.base,
  },
  dateButtonContent: {
    flex: 1,
  },
  dateLabel: {
    fontSize: theme.typography.fontSize.xs,
    color: theme.colors.textTertiary,
    fontWeight: theme.typography.fontWeight.medium,
    marginBottom: theme.spacing.xs / 2,
    textTransform: 'uppercase',
  },
  dateValue: {
    fontSize: theme.typography.fontSize.base,
    fontWeight: theme.typography.fontWeight.semibold,
    color: theme.colors.textPrimary,
  },
  dateSeparator: {
    width: 1,
    backgroundColor: theme.colors.borderLight,
    marginVertical: theme.spacing.xs,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: theme.spacing.base,
    paddingBottom: 80,
  },
  summaryContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: theme.spacing.base,
  },
  summaryCard: {
    flex: 1,
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.xl,
    padding: theme.spacing.base,
    marginHorizontal: theme.spacing.xs / 2,
    alignItems: 'center',
    ...theme.shadows.sm,
  },
  summaryIconContainer: {
    width: 48,
    height: 48,
    borderRadius: theme.borderRadius.full,
    backgroundColor: theme.colors.successLight,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: theme.spacing.base,
  },
  summaryLabel: {
    fontSize: theme.typography.fontSize.sm,
    color: theme.colors.textTertiary,
    fontWeight: theme.typography.fontWeight.medium,
    marginBottom: theme.spacing.xs,
    textAlign: 'center',
  },
  summaryValue: {
    fontSize: theme.typography.fontSize.lg,
    fontWeight: theme.typography.fontWeight.bold,
    color: theme.colors.textPrimary,
    textAlign: 'center',
  },
  chartCard: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.xl,
    padding: theme.spacing.base,
    marginBottom: theme.spacing.base,
    ...theme.shadows.md,
  },
  chartHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: theme.spacing.base,
  },
  chartTitle: {
    fontSize: theme.typography.fontSize.lg,
    fontWeight: theme.typography.fontWeight.bold,
    color: theme.colors.textPrimary,
    marginLeft: theme.spacing.xs,
  },
  chart: {
    marginVertical: theme.spacing.base,
    borderRadius: theme.borderRadius.md,
  },
  revenueListContainer: {
    marginTop: theme.spacing.xs,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: theme.spacing.base,
    paddingHorizontal: theme.spacing.xs,
  },
  sectionTitle: {
    fontSize: theme.typography.fontSize.lg,
    fontWeight: theme.typography.fontWeight.bold,
    color: theme.colors.textPrimary,
    marginLeft: theme.spacing.xs,
    flex: 1,
  },
  sectionSubtitle: {
    fontSize: theme.typography.fontSize.sm,
    color: theme.colors.textTertiary,
    fontWeight: theme.typography.fontWeight.regular,
  },
  revenueCard: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.xl,
    marginBottom: theme.spacing.base,
    overflow: 'hidden',
    ...theme.shadows.md,
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: theme.spacing.base,
    backgroundColor: theme.colors.backgroundSecondary,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.borderLight,
  },
  revenueIconContainer: {
    width: 40,
    height: 40,
    borderRadius: theme.borderRadius.md,
    backgroundColor: theme.colors.white,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: theme.spacing.base,
  },
  revenueTitle: {
    flex: 1,
    fontSize: theme.typography.fontSize.base,
    fontWeight: theme.typography.fontWeight.semibold,
    color: theme.colors.textPrimary,
  },
  totalBadge: {
    backgroundColor: theme.colors.success,
    paddingHorizontal: theme.spacing.base,
    paddingVertical: theme.spacing.xs,
    borderRadius: theme.borderRadius.md,
  },
  totalBadgeText: {
    color: theme.colors.white,
    fontSize: theme.typography.fontSize.base,
    fontWeight: theme.typography.fontWeight.bold,
  },
  cardBody: {
    padding: theme.spacing.base,
  },
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: theme.spacing.base,
  },
  detailItem: {
    flex: 1,
  },
  detailLabel: {
    fontSize: theme.typography.fontSize.xs,
    color: theme.colors.textTertiary,
    fontWeight: theme.typography.fontWeight.medium,
    marginBottom: theme.spacing.xs / 2,
    textTransform: 'uppercase',
  },
  detailValue: {
    fontSize: theme.typography.fontSize.base,
    fontWeight: theme.typography.fontWeight.semibold,
    color: theme.colors.textPrimary,
  },
  discountValue: {
    color: theme.colors.error,
  },
  taxContainer: {
    paddingTop: theme.spacing.base,
    borderTopWidth: 1,
    borderTopColor: theme.colors.borderLight,
  },
  taxRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: theme.spacing.xs,
  },
  taxLabel: {
    fontSize: theme.typography.fontSize.sm,
    color: theme.colors.textTertiary,
    marginLeft: theme.spacing.xs,
    marginRight: theme.spacing.base,
    flex: 1,
  },
  taxValue: {
    fontSize: theme.typography.fontSize.sm,
    fontWeight: theme.typography.fontWeight.medium,
    color: theme.colors.textPrimary,
  },
  roundOffValue: {
    color: theme.colors.error,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing['2xl'],
    minHeight: 300,
  },
  emptyText: {
    fontSize: theme.typography.fontSize.xl,
    fontWeight: theme.typography.fontWeight.semibold,
    color: theme.colors.textSecondary,
    marginTop: theme.spacing.base,
    marginBottom: theme.spacing.xs,
  },
  emptySubtext: {
    fontSize: theme.typography.fontSize.base,
    color: theme.colors.textTertiary,
    textAlign: 'center',
  },
});

export default RevenueSummary;

